import type { Sentence } from "../types";

/**
 * Reading-practice sentences for the fluency unit (l22). By that lesson the
 * entire inventory is taught, so any well-formed Hindi sentence is readable —
 * but each is still checked by `content-lint.test.ts`.
 *
 * These are the everyday phrases a heritage speaker already says out loud; the
 * goal here is only to read them. Every `devanagari` is NFC-normalized.
 */
export const sentences: Sentence[] = [
  {
    id: "s-namaste",
    devanagari: "नमस्ते।",
    iast: "namaste.",
    gloss: "Hello.",
  },
  {
    id: "s-how-are-you",
    devanagari: "आप कैसे हैं?",
    iast: "āp kaise haiṁ?",
    gloss: "How are you?",
  },
  {
    id: "s-my-name",
    devanagari: "मेरा नाम राहुल है।",
    iast: "merā nām rāhul hai.",
    gloss: "My name is Rahul.",
  },
  {
    id: "s-where-from",
    devanagari: "आप कहाँ से हैं?",
    iast: "āp kahāṁ se haiṁ?",
    gloss: "Where are you from?",
  },
  {
    id: "s-thank-you",
    devanagari: "बहुत बहुत धन्यवाद।",
    iast: "bahut bahut dhanyavād.",
    gloss: "Thank you very much.",
  },
  {
    id: "s-water-please",
    devanagari: "पानी दीजिए।",
    iast: "pānī dījie.",
    gloss: "Please give (me) water.",
  },
  {
    id: "s-i-am-hungry",
    devanagari: "मुझे भूख लगी है।",
    iast: "mujhe bhūkh lagī hai.",
    gloss: "I am hungry.",
  },
  {
    id: "s-good-morning",
    devanagari: "सुप्रभात।",
    iast: "suprabhāt.",
    gloss: "Good morning.",
  },
  {
    id: "s-see-you",
    devanagari: "फिर मिलेंगे।",
    iast: "phir mileṁge.",
    gloss: "See you again.",
  },
  {
    id: "s-this-is-my-house",
    devanagari: "यह मेरा घर है।",
    iast: "yah merā ghar hai.",
    gloss: "This is my house.",
  },
  {
    id: "s-food-is-ready",
    devanagari: "खाना तैयार है।",
    iast: "khānā taiyār hai.",
    gloss: "The food is ready.",
  },
  {
    id: "s-what-time",
    devanagari: "क्या समय हुआ है?",
    iast: "kyā samay huā hai?",
    gloss: "What time is it?",
  },
  {
    id: "s-i-love-you",
    devanagari: "मैं तुमसे प्यार करता हूँ।",
    iast: "maiṁ tumse pyār kartā hūṁ.",
    gloss: "I love you.",
  },
  {
    id: "s-come-home",
    devanagari: "घर आ जाओ।",
    iast: "ghar ā jāo.",
    gloss: "Come home.",
  },
  {
    id: "s-she-is-my-sister",
    devanagari: "वह मेरी बहन है।",
    iast: "vah merī bahan hai.",
    gloss: "She is my sister.",
  },
  {
    id: "s-lets-go",
    devanagari: "चलो चलते हैं।",
    iast: "calo calte haiṁ.",
    gloss: "Let's go.",
  },
  {
    id: "s-it-is-hot",
    devanagari: "आज बहुत गरमी है।",
    iast: "āj bahut garmī hai.",
    gloss: "It is very hot today.",
  },
  {
    id: "s-child-is-sleeping",
    devanagari: "बच्चा सो रहा है।",
    iast: "baccā so rahā hai.",
    gloss: "The child is sleeping.",
  },
  {
    id: "s-book-on-table",
    devanagari: "किताब मेज़ पर है।",
    iast: "kitāb mez par hai.",
    gloss: "The book is on the table.",
  },
  {
    id: "s-how-much",
    devanagari: "यह कितने का है?",
    iast: "yah kitne kā hai?",
    gloss: "How much is this?",
  },
  {
    id: "s-i-dont-know",
    devanagari: "मुझे नहीं पता।",
    iast: "mujhe nahīṁ patā.",
    gloss: "I don't know.",
  },
  {
    id: "s-please-sit",
    devanagari: "कृपया बैठिए।",
    iast: "kṛpayā baiṭhie.",
    gloss: "Please sit.",
  },
  {
    id: "s-tea-or-milk",
    devanagari: "चाय या दूध?",
    iast: "cāy yā dūdh?",
    gloss: "Tea or milk?",
  },
  {
    id: "s-take-care",
    devanagari: "अपना ख़याल रखना।",
    iast: "apnā khayāl rakhnā.",
    gloss: "Take care of yourself.",
  },
  {
    id: "s-i-am-fine",
    devanagari: "मैं ठीक हूँ।",
    iast: "maiṁ ṭhīk hūṁ.",
    gloss: "I am fine.",
  },
  {
    id: "s-what-is-your-name",
    devanagari: "आपका नाम क्या है?",
    iast: "āpkā nām kyā hai?",
    gloss: "What is your name?",
  },
  {
    id: "s-i-am-from-india",
    devanagari: "मैं भारत से हूँ।",
    iast: "maiṁ bhārat se hūṁ.",
    gloss: "I am from India.",
  },
  {
    id: "s-lets-eat",
    devanagari: "चलो खाना खाते हैं।",
    iast: "calo khānā khāte haiṁ.",
    gloss: "Let's eat.",
  },
  {
    id: "s-the-water-is-cold",
    devanagari: "पानी ठंडा है।",
    iast: "pānī ṭhaṇḍā hai.",
    gloss: "The water is cold.",
  },
  {
    id: "s-i-am-going-home",
    devanagari: "मैं घर जा रहा हूँ।",
    iast: "maiṁ ghar jā rahā hūṁ.",
    gloss: "I am going home.",
  },
  {
    id: "s-good-night",
    devanagari: "शुभ रात्रि।",
    iast: "śubh rātri.",
    gloss: "Good night.",
  },
  {
    id: "s-please-come-in",
    devanagari: "अंदर आइए।",
    iast: "andar āie.",
    gloss: "Please come in.",
  },
];

/** Every sentence indexed by its stable id. */
export const sentenceById: ReadonlyMap<string, Sentence> = new Map(
  sentences.map((sentence) => [sentence.id, sentence]),
);
